import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

import { authService } from '../services/authService';
import { saveTask, removeTask, getTasksForComponent } from '../services/taskPersistence';
import { protocolOwner, belongsTo } from '../lib/protocolOwner';
import { logUiEvent } from '../services/uiLogger';
import { promptService } from '../services/promptService';
import { getLastPromptId, setLastPromptId, resolveInitialPromptId } from '../services/promptPreference';
import { PASTED_SOURCE_NAME } from '../lib/manualText';
import SettingsBar, { ProtocolActions } from './protocol/SettingsBar';
import { useIsMobile } from '../hooks/useIsMobile';
import TranscriptionTab from './protocol/TranscriptionTab';
import ProtocolTab from './protocol/ProtocolTab';
import ChatPanel from './protocol/ChatPanel';
import PromptEditorModal from './protocol/PromptEditorModal';
import ShareModal from './ShareModal';
import { historyService } from '../services/historyService';
import EditSpeakersModal from './EditSpeakersModal';
import { extractSpeakerLabels, applySpeakerRename, cleanSpeakerName, looksDiarized } from './protocol/speakerLabels';
import { voiceService } from '../services/voiceService';
import VoiceIdBar from './VoiceIdBar';
import { describeTaskError, describeStartError } from './protocol/summarizeError';
import { ENDPOINTS } from '../config/endpoints';

const API_BASE_URL = ENDPOINTS.summarize;

// Уровни объёма саммари. «Короче»/«Подробнее» двигают индекс; директива
// дописывается к выбранному промпту, протокол ПЕРЕСОБИРАЕТСЯ из транскрипта
// на новом уровне (не «раздуваем» готовый текст → без галлюцинаций).
//
// 🔴 `label` — по-русски, `hint` обязателен. Первый уровень назывался «TL;DR»,
// и 08.09.2026 пользователь написал, что не понимает, что это значит. Слово
// знакомо разработчику, а не руководителю на планёрке. Остальные подписи
// («Кратко», «Средне») понятны на слух, но не говорят, сколько это в словах —
// подсказка снимает и это. Числа в hint берутся из самой директивы, чтобы они
// не разошлись: правишь одно — правь второе.
//
// `directive` намеренно оставлена как была, включая «TL;DR» внутри: это текст
// для модели, она его понимает, и переписывание меняло бы длину протоколов
// у всех разом.
const DETAIL_LEVELS = [
  { label: 'В двух словах', hint: '2–3 предложения, до 50 слов, одним абзацем', directive: 'ОБЪЁМ — TL;DR (приоритетнее структуры промпта): строго 2–3 предложения, ≤50 слов, один абзац, БЕЗ заголовков/разделов/списков. Только главный вывод и ключевое решение. Полнотой структуры жертвуй ради краткости.' },
  { label: 'Кратко', hint: 'до 120 слов, 4–6 пунктов', directive: 'ОБЪЁМ — КРАТКО (приоритетнее структуры промпта): ≤120 слов, 4–6 буллетов, только самое важное, без под-разделов и вложенных списков. Второстепенное опусти.' },
  { label: 'Средне', hint: 'до 250 слов, 2–4 коротких раздела', directive: 'ОБЪЁМ — СРЕДНЕ: ≤250 слов, основные моменты в 2–4 коротких разделах, без избыточной детализации.' },
  { label: 'Подробно', hint: '400–600 слов, все разделы шаблона', directive: 'ОБЪЁМ — ПОДРОБНО: ~400–600 слов, раскрой все разделы промпта с ключевыми моментами и важными деталями.' },
  { label: 'Максимально', hint: 'без ограничения по длине, со всеми деталями', directive: 'ОБЪЁМ — МАКСИМАЛЬНО ПОДРОБНО: без верхнего ограничения, раскрой всё детально по всем разделам, с нюансами; ничего важного не опускай.' },
];
const DEFAULT_LEVEL = 2;

// Глобальное правило вывода: только документ, без ассистентской болтовни
// (вступлений «Ниже пересказ…» и предложений «если хотите, могу ещё…»).
const OUTPUT_RULE = 'Выводи ТОЛЬКО сам документ. Без вступительных фраз («Ниже пересказ…», «Вот …»), без обращений к пользователю и без предложений сделать что-то ещё в конце. Начинай сразу с содержания.';

const MeetingProtocol = ({ state, setState, shareItemId, onRenameSpeakers,
                          onSpeakerConfirmed, resolveRecordId, onSavedElsewhere,
                          historyEnabled, onEnableHistory, onGoToRecognition }) => {
  const transcriptionText = state.transcriptionText || '';
  const [resultText, setResultText] = useState(state.resultText || '');
  // Кем изменён текущий текст протокола после сборки: 'assistant' — правка из
  // чата, 'manual' — руками, null — как собран. Подпись над документом и
  // `source` протокола в Истории. Прежние тексты — стек для «Отменить».
  const [changedBy, setChangedBy] = useState(state.changedBy || null);
  const undoRef = useRef([]);
  const applyProtocolText = (text, source) => {
    if (text === resultText) return;
    // Ручная правка идёт по клавише: в стек уходит текст ДО начала правки, а
    // не каждая буква — «Отменить» возвращает к тому, что было до неё.
    if (!(source === 'manual' && changedBy === 'manual')) {
      undoRef.current.push({ text: resultText, source: changedBy });
    }
    setResultText(text);
    setChangedBy(source);
  };
  const undoProtocolText = () => {
    const previous = undoRef.current.pop();
    if (!previous) return;
    setResultText(previous.text);
    setChangedBy(previous.source);
  };
  const resetProtocolHistory = () => {
    undoRef.current = [];
    setChangedBy(null);
  };
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [taskStatus, setTaskStatus] = useState(null);
  const [error, setError] = useState(null);
  // Повтор предлагаем только при перегрузке модели — см. protocol/summarizeError.js.
  // Признак ставится ТОЛЬКО вместе с текстом (showError): пока он жил отдельным
  // состоянием, кнопка «Повторить» оставалась от прошлой ошибки и предлагала
  // повтор, который уже ничего не делал.
  const [canRetry, setCanRetry] = useState(false);

  const showError = (message, retry = false) => {
    setError(message);
    setCanRetry(retry);
  };

  const [prompts, setPrompts] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState(state.selectedPromptId || null);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [defaultPromptId, setDefaultPromptId] = useState(null);
  // 🔴 Модель больше не выбирается человеком: кнопка «В облаке» отправляла
  // расшифровку внешней модели за пределы России вопреки нашему же согласию
  // на обработку персональных данных (решение владельца 21.09.2026). Значение
  // осталось полем, потому что попадает в запись истории протокола; запасную
  // модель при перегрузке выбирает сервис, и в запросе её нет.
  const selectedModel = 'llama';
  const [detailLevel, setDetailLevel] = useState(
    typeof state.detailLevel === 'number' ? state.detailLevel : DEFAULT_LEVEL);

  const [activeTab, setActiveTab] = useState('transcription');
  // Телефон: чат — третьей вкладкой рядом с документом, настройки свёрнуты.
  // Колонкой всё сразу не помещалось, и документ сжимался до нуля (27.09).
  const isMobile = useIsMobile();
  const [mobilePane, setMobilePane] = useState('doc');
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Набор протоколов записи. У одной встречи их бывает несколько — по разным
  // шаблонам и с разной подробностью; bit-history это умеет с самого начала
  // (`POST /items/{id}/summaries`, модуль `app/summaries.py`), а экран показывал
  // ровно один и молча затирал предыдущий. Предел — пять: больше на одну строку
  // вкладок не помещается, да и сравнивать столько уже незачем.
  const [protocols, setProtocols] = useState(() => state.protocols || []);
  const [activeKey, setActiveKey] = useState(() => state.activeProtocolKey || null);
  // Следующая сборка кладётся РЯДОМ, а не поверх активной: взводится кнопкой
  // «Ещё протокол». Ref, а не состояние, — читается внутри поллинга.
  const addNextRef = useRef(false);
  // Реплика ассистента может прийти в закрытую панель — тогда о ней надо
  // сообщить точкой на кнопке, иначе это та же непрочитанная подсказка.
  const [editorModal, setEditorModal] = useState({ open: false, prompt: null });
  const pollIntervalRef = useRef(null);
  // Чей источник сейчас на экране. Задача протокола запоминает владельца при
  // запуске, и результат принимается на экран только своим источником: иначе
  // протокол новой встречи ложился к записи, открытой из Истории (24.09).
  const ownerRef = useRef(null);
  ownerRef.current = protocolOwner(state, shareItemId);
  // Фоновые доставки чужих протоколов — гасятся вместе с экраном, задача при
  // этом остаётся в localStorage и доедет при следующем открытии.
  const deliveryTimersRef = useRef([]);
  // Экран жив. Запрос, ушедший до размонтирования, возвращается уже в мёртвый
  // экран: без этой проверки цепочка опроса жила бы дальше, а при возврате на
  // экран запускалась бы вторая — и протокол ложился бы в запись дважды.
  const aliveRef = useRef(true);
  // App передаёт resolveRecordId новой стрелкой на каждом рендере, а цепочки
  // доставки заведены при монтаже: читаем свежую, иначе запись, сохранённая
  // уже после монтажа, для них так и оставалась бы 'pending'.
  const resolveRecordIdRef = useRef(resolveRecordId);
  resolveRecordIdRef.current = resolveRecordId;
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [share, setShare] = useState(null);
  const [shareTitle, setShareTitle] = useState('');
  const [speakersOpen, setSpeakersOpen] = useState(false);
  // Узнавание участников по голосу. Переименовывают спикеров чаще всего именно
  // здесь: после свежей транскрибации приложение само открывает «Протокол».
  const [voiceIdEnabled, setVoiceIdEnabled] = useState(false);
  const voiceStatusAskedRef = useRef(false);

  // Sync to parent
  useEffect(() => {
    setState((prev) => ({
      ...prev,
      resultText,
      changedBy,
      selectedPromptId,
      selectedModel,
      detailLevel,
      protocols,
      activeProtocolKey: activeKey,
    }));
  }, [resultText, changedBy, selectedPromptId, selectedModel, detailLevel, protocols, activeKey, setState]);

  // Load prompts + settings
  useEffect(() => {
    loadPromptsAndSettings();
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    aliveRef.current = true;
    const timers = deliveryTimersRef.current;   // массив один на весь век экрана
    return () => {
      aliveRef.current = false;
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      timers.forEach(clearTimeout);
    };
  }, []);

  // Протокол чужого источника — в его запись Истории, мимо экрана. Записи нет
  // (История выключена, вставленный текст) — задачу не трогаем: она дождётся
  // своего источника или истечёт через час, но чужую запись не испортит.
  const deliverElsewhere = async (task, summary) => {
    const recordId = task.owner?.recordId || resolveRecordIdRef.current?.(task.owner) || null;
    if (!recordId) return false;
    // Забираем задачу из хранилища ДО запроса, синхронно: второй экран или
    // вторая цепочка её уже не найдут и тот же протокол второй раз не допишут.
    if (!getTasksForComponent('protocol').some((t) => t.taskId === task.taskId)) return true;
    removeTask(task.taskId);
    try {
      await historyService.addSummary(recordId, {
        template: String(task.promptId || ''),
        model: String(task.model || ''),
        text: summary,
        source: '',
      });
      if (onSavedElsewhere) onSavedElsewhere(recordId);
    } catch (_) {
      saveTask(task);   // не дописали — вернём, повторим при следующем открытии
    }
    return true;
  };

  const watchElsewhere = async (task) => {
    try {
      const resp = await axios.get(task.statusEndpoint, { headers: authService.getAuthHeaders() });
      if (!aliveRef.current) return;
      if (resp.data.status === 'completed') {
        const result = await axios.get(task.resultEndpoint, { headers: authService.getAuthHeaders() });
        if (!aliveRef.current) return;
        await deliverElsewhere(task, result.data.summary);
      } else if (resp.data.status === 'failed') {
        removeTask(task.taskId);
      } else {
        deliveryTimersRef.current.push(setTimeout(() => watchElsewhere(task), 3000));
      }
    } catch (_) { /* сеть — повторим при следующем открытии экрана */ }
  };

  // Восстановление задачи после обновления страницы
  useEffect(() => {
    const saved = getTasksForComponent('protocol');
    if (!saved.length) return;
    // На экран — только задача своего источника, чужие доезжают в свои записи.
    const mine = saved.filter((t) => belongsTo(t.owner, ownerRef.current));
    saved.filter((t) => !mine.includes(t)).forEach(watchElsewhere);
    if (!mine.length) return;
    const task = mine[0];
    (async () => {
      try {
        const resp = await axios.get(task.statusEndpoint, {
          headers: authService.getAuthHeaders(),
        });
        const { status, progress: prog } = resp.data;
        if (status === 'completed') {
          const result = await axios.get(task.resultEndpoint, {
            headers: authService.getAuthHeaders(),
          });
          acceptResult(result.data.summary);
          removeTask(task.taskId);
        } else if (status === 'failed') {
          const { message, canRetry: retry } = describeTaskError(resp.data.error_code, resp.data.error_detail);
          showError(message, retry);
          removeTask(task.taskId);
        } else {
          // PENDING / IN_PROGRESS — возобновляем поллинг
          setCurrentTaskId(task.taskId);
          setIsProcessing(true);
          setProgress(prog || 0);
          setTaskStatus(status);
          pollProgress(task.taskId, task);
        }
      } catch {
        removeTask(task.taskId);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPromptsAndSettings = async () => {
    try {
      const [promptsList, settings] = await Promise.all([
        promptService.list(),
        promptService.getSettings(),
      ]);
      setPrompts(promptsList);
      setDefaultPromptId(settings.default_prompt_id);

      const id = resolveInitialPromptId({
        sessionPromptId: selectedPromptId,
        lastPromptId: getLastPromptId(),
        defaultPromptId: settings.default_prompt_id,
        prompts: promptsList,
      });
      if (id) {
        setSelectedPromptId(id);
        setSelectedPrompt(promptsList.find((p) => p._id === id));
      }
    } catch (err) {
      showError('Ошибка загрузки шаблонов');
    }
  };

  // Ручная подача текста, когда транскрибации нет (вставка из буфера / файл).
  const handleManualText = (text, fileName) => {
    setResultText('');  // новый источник → прошлый протокол неактуален (сброс и локально, и в protocolState)
    setProtocols([]);
    setActiveKey(null);
    setState((prev) => ({
      ...prev,
      transcriptionText: text,
      rawTranscriptionText: undefined,
      sourceFileName: fileName || PASTED_SOURCE_NAME,
      sourceTaskId: null,
      meetingAt: null,   // свой текст — дата встречи неизвестна
      resultText: '',
      savedSummaryText: null,
      standaloneSavedText: null,
      standaloneItemId: null,
    }));
    setActiveTab('transcription');
    showError(null);
  };

  const MAX_PROTOCOLS = 5;

  // Готовый протокол лёг на экран. Либо заменяет активный («Собрать заново»),
  // либо встаёт рядом новой вкладкой («Ещё протокол») — и тогда App сохранит
  // его в запись через POST, а не PATCH (`addNewSummary`).
  const acceptResult = (textOfProtocol) => {
    setResultText(textOfProtocol);
    resetProtocolHistory();                       // собран заново — правок ещё нет
    const entry = {
      key: String(Date.now()),
      text: textOfProtocol,
      promptId: selectedPromptId,
      model: selectedModel,
      level: detailLevel,
      at: new Date().toISOString(),
    };
    setProtocols((prev) => {
      if (addNextRef.current || !prev.length) {
        setActiveKey(entry.key);
        return prev.concat(entry).slice(-MAX_PROTOCOLS);
      }
      const at = prev.findIndex((it) => it.key === activeKey);
      const index = at >= 0 ? at : prev.length - 1;
      const next = prev.slice();
      next[index] = { ...entry, key: prev[index].key };
      setActiveKey(prev[index].key);
      return next;
    });
    addNextRef.current = false;
    setActiveTab('protocol');
  };

  // Открыть другой протокол набора. savedSummaryText двигаем вместе с текстом:
  // без этого эффект сохранения в App увидел бы «новый текст» и переписал бы
  // выбранный протокол записи содержимым соседнего.
  const openProtocol = (entry) => {
    setActiveKey(entry.key);
    setResultText(entry.text);
    resetProtocolHistory();
    setState((prev) => ({ ...prev, savedSummaryText: entry.text }));
    setSelectedPromptId(entry.promptId || null);
    if (typeof entry.level === 'number') setDetailLevel(entry.level);
    setActiveTab('protocol');
  };

  // «Ещё протокол» — не сборка, а подготовка к ней: возвращаем экран к
  // настройкам и помечаем, что следующий результат ляжет рядом с прежними.
  const startAnotherProtocol = () => {
    addNextRef.current = true;
    setResultText('');
    setActiveKey(null);
    setActiveTab('transcription');
    setState((prev) => ({ ...prev, addNewSummary: true }));
    showError(null);
  };

  // Подпись вкладки: имя шаблона и время. Имена бывают до 41 знака — в строку
  // из пяти вкладок такое не встаёт, поэтому режем, а полное даём подсказкой.
  const protocolTitle = (entry) => {
    const prompt = prompts.find((it) => it._id === entry.promptId);
    const name = (prompt && prompt.name) || 'Протокол';
    const at = entry.at ? new Date(entry.at) : null;
    const time = at && !Number.isNaN(at.getTime())
      ? `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`
      : '';
    return { full: time ? `${name} · ${time}` : name, short: name.length > 18 ? `${name.slice(0, 17)}…` : name, time };
  };

  // Очистка исходного текста — возвращает пустое состояние (кнопки ввода).
  const handleClearText = () => {
    setResultText('');  // очистка источника → сбрасываем и протокол (локально + в protocolState)
    setProtocols([]);
    setActiveKey(null);
    setState((prev) => ({
      ...prev,
      transcriptionText: '',
      rawTranscriptionText: undefined,
      sourceFileName: '',
      sourceTaskId: null,
      meetingAt: null,
      resultText: '',
      savedSummaryText: null,
      standaloneSavedText: null,
      standaloneItemId: null,
    }));
    setActiveTab('transcription');
  };

  const handleSelectPrompt = (prompt) => {
    setSelectedPromptId(prompt._id);
    setSelectedPrompt(prompt);
    setLastPromptId(prompt._id);  // запоминаем на будущие сессии (переживает закрытие браузера)
  };

  // Открыть «Поделиться»: лениво подтянуть актуальную ссылку записи (если ею
  // уже делились из Истории). best-effort — при сбое модалка работает в режиме
  // «создать ссылку».
  const openShare = async () => {
    setShareOpen(true);
    if (!shareItemId) return;
    try {
      const item = await historyService.get(shareItemId);
      setShare(item?.share || null);
      setShareTitle(item?.title || '');            // чем делимся — видно в модалке
    } catch (_) { /* оставляем share как есть */ }
  };

  // Метки спикеров из текущего транскрипта (для кнопки и модалки).
  const speakerLabels = extractSpeakerLabels(transcriptionText);
  // Переименовывать участников есть смысл только там, где они РАЗМЕЧЕНЫ. Сами
  // метки этого не доказывают: «Итак:», «Вопрос:», «Ссылка:» в обычной речи
  // выглядят для разбора точно так же, и кнопка вылезала на тексте без всякой
  // диаризации. Достоверный признак — карта «метка → id спикера», она есть
  // только у пришедшего с распознавания; текст из Истории приходит без неё,
  // поэтому рядом стоит разбор самой разметки.
  const hasSpeakers = Object.keys(state.speakerIdByLabel || {}).length > 0
    || (speakerLabels.length > 0 && looksDiarized(transcriptionText));

  // Доступность узнавания голоса спрашиваем лениво и один раз: у большинства
  // аккаунтов функции нет, а лишний вызов на каждое открытие экрана не нужен.
  const askVoiceStatus = () => {
    if (voiceStatusAskedRef.current) return;
    voiceStatusAskedRef.current = true;
    voiceService.status().then(setVoiceIdEnabled);
  };

  // Применить переименование ТОЛЬКО к транскрипту (готовый протокол не трогаем —
  // он становится неактуальным, пользователь пересоберёт).
  const handleRenameSpeakers = (map, remembered = []) => {
    if (remembered.length && state.sourceTaskId) {
      const byLabel = state.speakerIdByLabel || {};
      const assignments = {};
      const unmapped = [];
      remembered.forEach((label) => {
        const speakerId = byLabel[label];
        const name = cleanSpeakerName(map[label]);
        if (speakerId && name) assignments[speakerId] = name;
        else unmapped.push(label);
      });
      if (unmapped.length) {
        // Тихо потерять «запомнить голос» нельзя: человек уверен, что сохранил.
        showError('Не удалось запомнить голос: ' + unmapped.join(', '));
      }
      if (Object.keys(assignments).length) {
        voiceService.remember(state.sourceTaskId, assignments).then((result) => {
          const skipped = [
            ...(result?.skipped_short || []).map((n) => `${n} — мало речи в этой записи`),
            ...(result?.skipped_mismatch || []).map((n) => `${n} — голос не похож на сохранённый под этим именем`),
            ...(result?.skipped_unavailable || []).map((n) => `${n} — данные этой записи уже недоступны`),
          ];
          // Частичный успех — тоже повод сказать: раньше сохранение одного из
          // двух выглядело как полный успех, и вторая галочка терялась молча.
          if (skipped.length) {
            showError('Не запомнили: ' + skipped.join('; ') + '.');
          } else if (!result?.saved) {
            showError('Голос не запомнен: данные этой записи уже недоступны '
              + '(отпечатки ждут решения 7 дней).');
          }
        }).catch((err) => {
          showError('Не удалось запомнить голос: '
            + (err.response?.data?.detail || err.message));
        });
      }
    }
    // Тот же map — во вкладку транскрибации: там своя копия текста, и без
    // этого человек, вернувшись туда, видел старые имена.
    if (onRenameSpeakers) onRenameSpeakers(map, 'protocol');
    setState((prev) => ({
      ...prev,
      transcriptionText: applySpeakerRename(prev.transcriptionText || '', map),
      rawTranscriptionText: prev.rawTranscriptionText
        ? applySpeakerRename(prev.rawTranscriptionText, map)
        : prev.rawTranscriptionText,
      // Метки после переименования другие — карта «метка → спикер диаризации»
      // должна ехать за ними, иначе второй заход ничего не запомнит.
      speakerIdByLabel: Object.fromEntries(
        Object.entries(prev.speakerIdByLabel || {}).map(([label, speakerId]) => {
          const renamed = (map[label] || '').trim();
          return [renamed || label, speakerId];
        })),
    }));
  };

  const handleSetDefault = async (promptId) => {
    try {
      await promptService.updateSettings({ default_prompt_id: promptId });
      setDefaultPromptId(promptId);
    } catch (e) {
      showError('Ошибка сохранения настроек');
    }
  };

  const handleDeletePrompt = async (promptId) => {
    if (!window.confirm('Удалить этот шаблон?')) return;
    try {
      await promptService.remove(promptId);
      setPrompts((prev) => prev.filter((p) => p._id !== promptId));
      if (selectedPromptId === promptId) {
        setSelectedPromptId(null);
        setSelectedPrompt(null);
      }
    } catch (e) {
      showError('Ошибка удаления шаблона');
    }
  };

  const handleClonePrompt = async (promptId) => {
    try {
      const cloned = await promptService.clone(promptId);
      setPrompts((prev) => [...prev, cloned]);
    } catch (e) {
      showError('Ошибка копирования шаблона');
    }
  };

  const doProcess = async (prompt, model, level = detailLevel) => {
    if (!transcriptionText || !prompt) {
      showError('Нужны текст расшифровки и шаблон протокола');
      return;
    }
    setIsProcessing(true);
    setProgress(0);
    showError(null);
    setResultText('');
    setTaskStatus(null);
    setActiveTab('protocol');

    const directive = DETAIL_LEVELS[level]?.directive || '';
    const systemPrompt = [prompt.text, directive, OUTPUT_RULE].filter(Boolean).join('\n\n');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/tasks`,
        {
          text: transcriptionText,
          system_prompt: systemPrompt,
          model,
          // Когда была встреча: бот и «Записать сейчас» знают начало, у загруженного
          // файла даты нет — сервис тогда пишет «дата неизвестна» (App.js meetingAt).
          meeting_at: state.meetingAt || null,
        },
        { headers: authService.getAuthHeaders() }
      );
      const tid = response.data.task_id;
      setCurrentTaskId(tid);
      const task = {
        taskId: tid,
        service: 'bit-llm-summarize',
        statusEndpoint: `${API_BASE_URL}/tasks/${tid}/status`,
        resultEndpoint: `${API_BASE_URL}/tasks/${tid}/result`,
        fileName: '',
        startedAt: new Date().toISOString(),
        component: 'protocol',
        promptId: prompt._id,
        model,
        owner: ownerRef.current,
      };
      saveTask(task);
      pollProgress(tid, task);
    } catch (err) {
      // Отказ показываем по коду и тексту сервиса, а не одной фразой на все
      // случаи: 429 «пакет тарифа исчерпан» и 503 «модель перегружена» требуют
      // от человека разного, а раньше выглядели одинаково.
      showError(describeStartError(err?.response?.status, err?.response?.data?.detail));
      setIsProcessing(false);
    }
  };

  const startProcessing = () => {
    doProcess(selectedPrompt, selectedModel, detailLevel);
  };

  // «Подробнее»/«Короче» — двигаем уровень и ПЕРЕСОБИРАЕМ из транскрипта.
  const handleMoreDetail = () => {
    if (isProcessing || detailLevel >= DETAIL_LEVELS.length - 1) return;
    const nl = detailLevel + 1;
    setDetailLevel(nl);
    doProcess(selectedPrompt, selectedModel, nl);
  };

  const handleLessDetail = () => {
    if (isProcessing || detailLevel <= 0) return;
    const nl = detailLevel - 1;
    setDetailLevel(nl);
    doProcess(selectedPrompt, selectedModel, nl);
  };

  const handleCancel = () => {
    logUiEvent('cancel', { component: 'meeting-protocol', task_id: currentTaskId });
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (currentTaskId) removeTask(currentTaskId);
    setCurrentTaskId(null);
    setIsProcessing(false);
    setProgress(0);
    setTaskStatus(null);
    setResultText('');
  };

  const pollProgress = (taskId, task) => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = setInterval(async () => {
      try {
        const resp = await axios.get(`${API_BASE_URL}/tasks/${taskId}/status`, {
          headers: authService.getAuthHeaders(),
        });
        setTaskStatus(resp.data.status);
        setProgress(resp.data.progress);

        if (resp.data.status === 'completed') {
          clearInterval(pollIntervalRef.current);
          const result = await axios.get(`${API_BASE_URL}/tasks/${taskId}/result`, {
            headers: authService.getAuthHeaders(),
          });
          // Экран закрыли, пока шёл запрос: результат принимать некому, а
          // задача должна остаться — её подхватит восстановление при возврате.
          if (!aliveRef.current) return;
          setProgress(100);
          setIsProcessing(false);
          // Источник сменился, пока собирали (вставили другой текст) — чужой
          // результат на экран не кладём.
          if (task && !belongsTo(task.owner, ownerRef.current)) {
            if (!(await deliverElsewhere(task, result.data.summary))) {
              showError('Протокол собран по прежнему тексту, а на экране уже другой — '
                + 'соберите протокол заново.');
            }
            return;
          }
          acceptResult(result.data.summary);
          removeTask(taskId);
        } else if (resp.data.status === 'failed') {
          clearInterval(pollIntervalRef.current);
          const { message, canRetry: retry } = describeTaskError(resp.data.error_code, resp.data.error_detail);
          showError(message, retry);
          setIsProcessing(false);
          removeTask(taskId);
        }
      } catch (e) {
        clearInterval(pollIntervalRef.current);
        showError('Ошибка при проверке прогресса');
        setIsProcessing(false);
        if (taskId) removeTask(taskId);
      }
    }, 3000);
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3">
      {/* Шапка настроек стоит ВСЕГДА и на одном месте. Раньше над готовым
          документом она пряталась, а объём переезжал в панель под ним: экран
          перестраивался на каждый шаг, и найти управление становилось задачей
          (решение владельца 07.09.2026). Дублей при этом нет — панель снизу
          убрана целиком, каждый контрол существует в одном экземпляре. */}
      <SettingsBar
        prompts={prompts}
        levels={DETAIL_LEVELS.map(({ label, hint }) => ({ label, hint }))}
        detailLevel={detailLevel}
        onSelectLevel={(i) => setDetailLevel(i)}
        selectedPromptId={selectedPromptId}
        defaultPromptId={defaultPromptId}
        isProcessing={isProcessing}
        taskStatus={taskStatus}
        progress={progress}
        onSelectPrompt={handleSelectPrompt}
        onClonePrompt={handleClonePrompt}
        onCreatePrompt={() => setEditorModal({ open: true, prompt: null })}
        onProcess={startProcessing}
        onCancel={handleCancel}
        onSetDefault={handleSetDefault}
        onEditPrompt={(prompt) => setEditorModal({ open: true, prompt })}
        onDeletePrompt={handleDeletePrompt}
        hasResult={!!resultText && !isProcessing}
        onNewProtocol={handleClearText}
        mobile={isMobile}
        expanded={settingsOpen}
        onToggle={() => setSettingsOpen((v) => !v)}
      />

      {error && (
        <div className="rounded-panel bg-paper-accent/[0.06] px-4 py-3 text-[13px] text-paper-accent flex items-center justify-between gap-3 shrink-0">
          <span>{error}</span>
          {canRetry && (
            <button type="button" onClick={startProcessing}
                    className="shrink-0 rounded-[8px] bg-paper-card px-3 py-1 text-paper-ink font-semibold">
              Повторить
            </button>
          )}
        </div>
      )}

      {/* До lg — колонка с растяжением, а не сетка: у сетки строки по
          содержимому, и документ под настройками схлопывался в полоску. */}
      <div className="flex flex-col lg:grid flex-1 min-h-0 gap-3 lg:grid-cols-[minmax(0,1fr)_332px]">
        <div className={`flex flex-col gap-3 min-h-0 ${isMobile && mobilePane === 'chat' ? 'hidden' : 'flex-1'} lg:flex-none`}>
          <section className={`flex-1 min-h-0 bg-paper-card rounded-card flex flex-col overflow-hidden ${
            isMobile && mobilePane === 'chat' ? 'hidden' : ''}`}>
            {/* Вкладки по артборду: заливка вместо подчёркиваний, «Расшифровка» —
                так экран называется в макете и так его называет человек. */}
            <div className="kbd-hide flex flex-wrap md:flex-nowrap items-center gap-2 px-4 pt-3 md:px-[22px] md:pt-[18px] pb-3 shrink-0">
              {/* Вкладки: расшифровка и по одной на каждый протокол записи.
                  Тот же язык, что в Истории (`HistoryViewer.js`), — там набор
                  протоколов живёт с самого начала, и заводить второй способ
                  показывать одно и то же незачем. */}
              <div className="flex flex-nowrap md:flex-wrap overflow-x-auto md:overflow-visible [&>*]:shrink-0 bg-paper-bg rounded-[10px] p-1 gap-1 min-w-0 max-w-full">
                <button
                  type="button"
                  onClick={() => { setMobilePane('doc'); setActiveTab('transcription'); }}
                  className={`rounded-[8px] px-[10px] py-[5px] text-[13px] font-medium transition-colors ${
                    activeTab === 'transcription' ? 'bg-paper-card text-paper-ink' : 'text-paper-ink-3'
                  }`}
                >
                  Расшифровка
                </button>

                {protocols.length === 0 && (
                  <button
                    type="button"
                    onClick={() => isProcessing && setActiveTab('protocol')}
                    disabled={!isProcessing}
                    title={!isProcessing ? 'Сначала соберите протокол' : undefined}
                    className={`rounded-[8px] px-[10px] py-[5px] text-[13px] font-medium transition-colors ${
                      activeTab === 'protocol' ? 'bg-paper-card text-paper-ink' : 'text-paper-hint'
                    }`}
                  >
                    Протокол
                  </button>
                )}

                {protocols.map((entry) => {
                  const label = protocolTitle(entry);
                  const active = activeTab === 'protocol' && entry.key === activeKey;
                  return (
                    <button
                      key={entry.key}
                      type="button"
                      title={label.full}
                      onClick={() => { setMobilePane('doc'); openProtocol(entry); }}
                      className={`rounded-[8px] px-[10px] py-[5px] text-[13px] font-medium transition-colors ${
                        active ? 'bg-paper-card text-paper-ink' : 'text-paper-ink-3 hover:text-paper-ink'
                      }`}
                    >
                      {protocols.length > 1 ? label.short : 'Протокол'}
                    </button>
                  );
                })}

                {/* «Чат» — до «+ Ещё протокол»: строка вкладок на телефоне листается
                    вбок, и последним он уезжал за край. */}
                {isMobile && (
                  <button type="button" onClick={() => setMobilePane('chat')}
                          className="rounded-[8px] px-[10px] py-[5px] text-[13px] font-medium text-paper-ink-3">
                    Чат
                  </button>
                )}

                {protocols.length > 0 && !isProcessing && (
                  <button
                    type="button"
                    disabled={protocols.length >= MAX_PROTOCOLS}
                    onClick={startAnotherProtocol}
                    title={protocols.length >= MAX_PROTOCOLS
                      ? `Больше ${MAX_PROTOCOLS} протоколов на одну запись не держим`
                      : 'Собрать ещё один по другому шаблону'}
                    className={`rounded-[8px] px-[10px] py-[5px] text-[13px] font-medium ${
                      protocols.length >= MAX_PROTOCOLS ? 'text-paper-hint' : 'text-paper-ink-3 hover:text-paper-ink'
                    }`}
                  >
                    {isMobile ? '+ Ещё' : '+ Ещё протокол'}
                  </button>
                )}

              </div>

              <div className="flex-1" />

              {/* Подпись «кем изменён» и «Отменить» — по артборду «Бумаги»:
                  правка ассистента не переписывает протокол молча, шаг назад
                  всегда под рукой. */}
              {activeTab === 'protocol' && changedBy && (
                <span className="flex items-center gap-2 text-[13px] text-paper-ink-3" data-testid="protocol-changed">
                  {changedBy === 'assistant' ? 'Правка ассистента применена' : 'Изменено вручную'}
                  <button type="button" onClick={undoProtocolText}
                          className="underline underline-offset-[3px] text-paper-ink">
                    Отменить
                  </button>
                </span>
              )}
              {/* На телефоне у расшифровки своя кнопка в панели — здесь не дублируем. */}
              {hasSpeakers && !(isMobile && activeTab === 'transcription') && (
                <button type="button" onClick={() => { askVoiceStatus(); setSpeakersOpen(true); }}
                        className="text-[13px] underline underline-offset-[3px] text-paper-ink">
                  Имена участников
                </button>
              )}
              {activeTab === 'protocol' && shareItemId && (
                <button type="button" onClick={openShare}
                        className={`rounded-[10px] px-3 py-[6px] text-[13px] font-semibold ${
                          share ? 'bg-paper-bg text-paper-ink' : 'bg-paper-accent text-white'
                        }`}>
                  Дать ссылку
                </button>
              )}
              {/* Ссылка живёт на записи Истории, а при выключенной Истории записи
                  нет — кнопка пропадала молча, и человек искал её два дня
                  (дизайн-спринт 23.09). Включение сохранит и этот протокол. */}
              {activeTab === 'protocol' && !shareItemId && historyEnabled === false
                && resultText && onEnableHistory && (
                <button type="button" onClick={onEnableHistory} data-testid="enable-history-for-link"
                        className="rounded-[10px] bg-paper-bg px-3 py-[6px] text-[13px] font-semibold text-paper-ink">
                  Включить Историю, чтобы дать ссылку
                </button>
              )}
            </div>

            <div className="flex-1 min-h-0 overflow-hidden px-4 pb-3 md:px-[22px] md:pb-[18px]">
              {activeTab === 'transcription' ? (
                // Колонка, а не просто соседи: TranscriptionTab внутри берёт h-full,
                // и вместе с полосой они вылезали за пределы overflow-hidden — низ
                // расшифровки обрезался и был недостижим прокруткой.
                <div className="flex flex-col h-full">
                  <VoiceIdBar
                    recognized={state.recognizedSpeakers}
                    taskId={state.sourceTaskId}
                    dismissKey={state.sourceFileName}
                    onCorrect={() => { askVoiceStatus(); setSpeakersOpen(true); }}
                    onConfirmed={(speakerId) => {
                      setState((prev) => ({
                        ...prev,
                        recognizedSpeakers: (prev.recognizedSpeakers || []).map(
                          (item) => (item.speakerId === speakerId ? { ...item, confirmed: true } : item)),
                      }));
                      if (onSpeakerConfirmed) onSpeakerConfirmed(speakerId, 'protocol');
                    }}
                  />
                  <div className="flex-1 min-h-0">
                    <TranscriptionTab text={transcriptionText} rawText={state.rawTranscriptionText}
                      sourceFileName={state.sourceFileName} onManualText={handleManualText}
                      onClear={handleClearText} onGoToRecognition={onGoToRecognition} canEditSpeakers={hasSpeakers}
                      onEditSpeakers={() => { askVoiceStatus(); setSpeakersOpen(true); }}
                      recognizedLabels={(state.recognizedSpeakers || []).map((item) => item.label)} />
                  </div>
                </div>
              ) : (
                <ProtocolTab
                  resultText={resultText}
                  isProcessing={isProcessing}
                  taskStatus={taskStatus}
                  onResultChange={(text) => applyProtocolText(text, 'manual')}
                  onMoreDetail={handleMoreDetail}
                  onLessDetail={handleLessDetail}
                  canMore={detailLevel < DETAIL_LEVELS.length - 1}
                  canLess={detailLevel > 0}
                  levelLabel={DETAIL_LEVELS[detailLevel]?.label}
                  sourceFileName={state.sourceFileName}
                />
              )}
            </div>

          </section>
        </div>

        {isMobile && mobilePane === 'chat' && (
          <div className="kbd-hide flex bg-paper-card rounded-[10px] p-1 gap-1 shrink-0 self-start">
            <button type="button" onClick={() => setMobilePane('doc')}
                    className="rounded-[8px] px-[10px] py-[5px] text-[13px] font-medium text-paper-ink-3">
              {activeTab === 'protocol' ? 'Протокол' : 'Расшифровка'}
            </button>
            <span className="rounded-[8px] bg-paper-bg px-[10px] py-[5px] text-[13px] font-medium text-paper-ink">Чат</span>
          </div>
        )}
        {/* На телефоне чат не размонтируется при уходе на документ: иначе
            переписка пропадала бы на каждом переключении вкладки. */}
        <div className={isMobile ? (mobilePane === 'chat' ? 'flex-1 min-h-0 flex flex-col [&>aside]:flex-1' : 'hidden') : 'contents'}>
        <ChatPanel
          visible
          transcriptionText={transcriptionText}
          rawTranscriptionText={state.rawTranscriptionText}
          meetingAt={state.meetingAt || null}
          protocolText={resultText}
          activeTab={activeTab}
          model={selectedModel}
          canShare={!!shareItemId}
          onShare={openShare}
          onProtocolChange={applyProtocolText}
        />
        </div>
      </div>

      {isMobile && mobilePane === 'doc' && (
        <div className="kbd-hide shrink-0">
          <ProtocolActions isProcessing={isProcessing} taskStatus={taskStatus} progress={progress}
                           onCancel={handleCancel} hasResult={!!resultText && !isProcessing}
                           onNewProtocol={handleClearText}
                           onProcess={() => { setSettingsOpen(false); startProcessing(); }}
                           selectedPromptId={selectedPromptId} mobile />
        </div>
      )}

      <PromptEditorModal
        key={editorModal.prompt?._id || 'new'}
        open={editorModal.open}
        onClose={() => setEditorModal({ open: false, prompt: null })}
        prompt={editorModal.prompt}
        prompts={prompts}
        defaultPromptId={defaultPromptId}
        onClone={handleClonePrompt}
        onUse={handleSelectPrompt}
        onSaved={(saved) => {
          // Создан ли шаблон только что — по списку ДО сохранения: правка
          // опечатки в другом шаблоне выбор переключать не должна.
          const created = !prompts.some((p) => p._id === saved._id);
          setPrompts((prev) => {
            const idx = prev.findIndex((p) => p._id === saved._id);
            if (idx >= 0) {
              const copy = [...prev];
              copy[idx] = saved;
              return copy;
            }
            return [...prev, saved];
          });
          // Новый шаблон сразу выбран: человек создавал его, чтобы им
          // воспользоваться. Раньше после «Сохранить» выбранным оставался
          // прежний, и новый приходилось искать в списке (дизайн-спринт 23.09).
          // Правка уже выбранного — обновляем его текст, чтобы сборка шла по новому.
          if (created || saved._id === selectedPromptId) handleSelectPrompt(saved);
        }}
        onDeleted={(id) => {
          setPrompts((prev) => prev.filter((p) => p._id !== id));
          if (selectedPromptId === id) {
            setSelectedPromptId(null);
            setSelectedPrompt(null);
          }
        }}
        onDefaultChanged={(id) => setDefaultPromptId(id)}
      />

      {shareOpen && (
        <ShareModal
          itemId={shareItemId}
          title={shareTitle}
          available={['transcript', 'protocol']}
          share={share}
          onClose={() => setShareOpen(false)}
          onChanged={setShare}
        />
      )}

      <EditSpeakersModal
        isOpen={speakersOpen}
        onClose={() => setSpeakersOpen(false)}
        speakers={speakerLabels}
        onSave={handleRenameSpeakers}
        voiceIdEnabled={voiceIdEnabled}
        voiceIdReady={!!state.sourceTaskId}
      />
    </div>
  );
};

export default MeetingProtocol;
